import React from 'react';
import { calculateVPD } from '../../game/systems/environmental';
import { calculateWeeklyRP } from '../../game/systems/labor';
import { getTechNode } from '../../game/data/technology';

// ── Helper: environment gauge color ──────────────────────────────────────────
function envColor(actual, target, tolerance = 10) {
  if (!target) return 'var(--text-muted)';
  const pct = Math.abs(actual - target) / target * 100;
  if (pct < 5)  return 'var(--green)';
  if (pct < 15) return 'var(--yellow)';
  return 'var(--red)';
}

// ── Mini progress bar ─────────────────────────────────────────────────────────
function MiniBar({ value, color }) {
  const safeVal = Math.max(0, Math.min(100, value || 0));
  const c = color || (safeVal > 60 ? 'var(--green)' : safeVal > 30 ? 'var(--yellow)' : 'var(--red)');
  return (
    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
      <div style={{ height: '100%', width: `${safeVal}%`, background: c, borderRadius: 2, transition: 'width 0.3s' }} />
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHead({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 14, marginBottom: 6, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
      {children}
    </div>
  );
}

// ── Stat row ─────────────────────────────────────────────────────────────────
function StatRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  );
}

// ── Env grid cell ─────────────────────────────────────────────────────────────
function EnvCell({ icon, label, value, color }) {
  return (
    <div style={{ background: 'var(--border-light)', borderRadius: 6, padding: '7px 8px', textAlign: 'center' }}>
      <div style={{ fontSize: 14, marginBottom: 2 }}>{icon}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

// ── Main drawer ───────────────────────────────────────────────────────────────
export function RoomDrawer({ open, roomDef, room, state, dispatch, onClose }) {
  const translateX = open ? '0%' : '100%';

  // ── LOCKED MODE ──────────────────────────────────────────────────────────
  function renderLocked() {
    return (
      <div style={{ padding: '20px 16px' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{roomDef.unlockLabel || roomDef.id}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
            {roomDef.type === 'curing' && 'Cure your harvest to improve quality and shelf life.'}
            {roomDef.type === 'processing' && 'Stage 2 processing facility for advanced extraction.'}
            {roomDef.type === 'veg' && 'An additional veg room for faster plant cycling.'}
            {roomDef.type === 'flower' && 'An additional flower room for higher output.'}
            {roomDef.type === 'marketing' && 'Run campaigns, schedule tours, and build your brand. Requires D4 — Marketing Department research.'}
            {!['curing','processing','veg','flower','marketing'].includes(roomDef.type) && 'Unlock this room to expand your operation.'}
          </div>
          {roomDef.unlockCost && (
            <>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--yellow)', marginBottom: 12 }}>
                ${roomDef.unlockCost.toLocaleString()}
              </div>
              <button
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={state.cash < roomDef.unlockCost}
                onClick={() => {
                  dispatch({ type: 'UNLOCK_ROOM', payload: { roomId: roomDef.stateId, cost: roomDef.unlockCost } });
                  onClose();
                }}
              >
                {state.cash >= roomDef.unlockCost ? '🔓 Purchase Room' : `Need $${(roomDef.unlockCost - state.cash).toLocaleString()} more`}
              </button>
            </>
          )}
          {roomDef.techGate && !roomDef.unlockCost && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
              Requires <b>{roomDef.techGate}</b> research to unlock.
              <div style={{ marginTop: 8 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'research' } })}
                >
                  View Tech Tree →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LAB MODE ─────────────────────────────────────────────────────────────
  function renderLab() {
    if (!room) return null;
    const scientists = state.workers.filter(w => w.type === 'scientist' && w.assignedRoomId === 'room-lab');
    const rpPerWeek = calculateWeeklyRP(state.workers, room);
    const researchName = state.activeResearch ? getTechNode(state.activeResearch.nodeId)?.name : null;
    const researchProgress = state.activeResearch
      ? Math.min(100, (state.researchPoints / (state.activeResearch.cost || 1)) * 100)
      : 0;
    return (
      <div style={{ padding: '16px' }}>
        <SectionHead>Lab Status</SectionHead>
        <StatRow label="Scientists" value={scientists.length} />
        <StatRow label="RP/week" value={`${rpPerWeek} RP`} valueColor="var(--blue)" />
        <StatRow label="Total RP" value={`${Math.floor(state.researchPoints)} RP`} />
        {researchName && (
          <>
            <SectionHead>Active Research</SectionHead>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{researchName}</div>
            <MiniBar value={researchProgress} color="var(--blue)" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {Math.round(researchProgress)}% complete
            </div>
          </>
        )}
        <SectionHead>Equipment</SectionHead>
        {Object.entries(room.equipment || {}).map(([key, eq]) => (
          <div key={key} style={{ marginBottom: 8 }}>
            <StatRow
              label={eq.label || key}
              value={eq.installed ? '✓ Installed' : `$${(eq.cost || 0).toLocaleString()}`}
              valueColor={eq.installed ? 'var(--green)' : 'var(--text-muted)'}
            />
            {!eq.installed && (
              <button
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', marginTop: 2 }}
                disabled={state.cash < (eq.cost || 0)}
                onClick={() => dispatch({ type: 'BUY_LAB_EQUIPMENT', payload: { equipId: key } })}
              >
                Purchase
              </button>
            )}
          </div>
        ))}
        <div style={{ marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            style={{ width: '100%' }}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'lab' } })}
          >
            Open Full Lab View →
          </button>
        </div>
      </div>
    );
  }

  // ── CURING ROOM MODE ──────────────────────────────────────────────────────
  function renderCuring() {
    if (!room) return null;
    const env    = room.environment ?? {};
    const recipe = room.recipe     ?? { temperature: 18, humidity: 62 };
    const eq     = room.equipment  ?? {};

    const tempDev = recipe.temperature ? Math.abs(env.temperature - recipe.temperature) / recipe.temperature * 100 : 0;
    const humDev  = recipe.humidity    ? Math.abs(env.humidity    - recipe.humidity)    / recipe.humidity    * 100 : 0;
    const condBadge = tempDev < 5 && humDev < 5 ? '🟢 Optimal'
      : tempDev < 15 && humDev < 15 ? '🟡 Acceptable'
      : '🔴 Poor';

    const curingSlots = room.curingSlots ?? [];
    const curable = state.inventory.filter(i =>
      i.status !== 'sold' && i.status !== 'in_curing' && i.status !== 'in_processing'
      && (i.type ?? 'flower') === 'flower' && i.grams > 0
    );

    return (
      <div style={{ padding: '0 16px 20px' }}>
        <SectionHead>Environment</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
          <EnvCell icon="🌡️" label="Temp"
            value={`${(env.temperature ?? recipe.temperature).toFixed(1)}°C`}
            color={envColor(env.temperature, recipe.temperature)} />
          <EnvCell icon="💧" label="Humidity"
            value={`${Math.round(env.humidity ?? recipe.humidity)}%`}
            color={envColor(env.humidity, recipe.humidity)} />
        </div>
        <StatRow label="Condition" value={condBadge} />
        <StatRow label="Target" value={`${recipe.temperature}°C / ${recipe.humidity}%`} valueColor="var(--text-muted)" />

        <SectionHead>Cure Slots ({curingSlots.length}/4)</SectionHead>
        {curingSlots.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>No items curing. Add flower below.</div>
        )}
        {curingSlots.map(slot => (
          <div key={slot.inventoryId} style={{ background: 'var(--border-light)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 12 }}>{slot.strainId.replace(/-/g, ' ')}</span>
              <span style={{ fontSize: 11, color: slot.status === 'degrading' ? 'var(--red)' : 'var(--text-muted)' }}>
                Day {slot.curingDay}/14
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 4, fontSize: 11 }}>
              <span style={{ color: 'var(--text-muted)' }}>Terpenes: <b>{slot.terpeneProfile?.toFixed(1)}</b></span>
              <span style={{ color: 'var(--text-muted)' }}>Visual: <b>{slot.visualAppeal?.toFixed(1)}</b></span>
            </div>
            <MiniBar value={(slot.curingDay / 14) * 100} color={slot.status === 'degrading' ? 'var(--red)' : 'var(--green)'} />
            {slot.status === 'degrading' && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 3 }}>⚠ Mold risk: {slot.moldRisk.toFixed(0)}%</div>
            )}
            <button
              className="btn btn-secondary btn-sm"
              style={{ width: '100%', marginTop: 6 }}
              onClick={() => dispatch({ type: 'PULL_FROM_CURE', payload: { inventoryId: slot.inventoryId } })}
            >
              Pull Now
            </button>
          </div>
        ))}

        {curingSlots.length < 4 && curable.length > 0 && (
          <>
            <SectionHead>Add to Cure</SectionHead>
            {curable.map(item => (
              <button
                key={item.id}
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', marginBottom: 4, justifyContent: 'space-between' }}
                onClick={() => dispatch({ type: 'ADD_TO_CURE', payload: { inventoryId: item.id } })}
              >
                <span>{item.strainId.replace(/-/g, ' ')}</span>
                <span style={{ color: 'var(--text-muted)' }}>{item.grams}g · Q{item.qualityScore}</span>
              </button>
            ))}
          </>
        )}

        <SectionHead>Equipment</SectionHead>
        {!eq.humidifier?.installed && (
          <div style={{ marginBottom: 8 }}>
            <StatRow label="Humidifier" value={`$${(eq.humidifier?.cost ?? 4000).toLocaleString()}`} valueColor="var(--text-muted)" />
            <button
              className="btn btn-secondary btn-sm"
              style={{ width: '100%', marginTop: 2 }}
              disabled={state.cash < (eq.humidifier?.cost ?? 4000)}
              onClick={() => dispatch({ type: 'BUY_CURING_EQUIPMENT', payload: { equipId: 'humidifier' } })}
            >
              Buy Humidifier
            </button>
          </div>
        )}
        {eq.humidifier?.installed && <StatRow label="Humidifier" value="✓ Installed" valueColor="var(--green)" />}
      </div>
    );
  }

  // ── PROCESSING ROOM MODE ──────────────────────────────────────────────────
  function renderProcessing() {
    if (!room) return null;
    const eq    = room.equipment ?? {};
    const queue = room.processingQueue ?? [];

    const hasRosin    = eq.rosinPress?.installed;
    const hasBHO      = eq.bhoExtractor?.installed;
    const hasE1       = state.unlockedTech?.includes('E1');
    const hasE2       = state.unlockedTech?.includes('E2');

    const available = state.inventory.filter(i =>
      i.status !== 'sold' && i.status !== 'in_processing' && i.status !== 'in_curing'
      && i.grams > 0
    );

    return (
      <div style={{ padding: '0 16px 20px' }}>
        <SectionHead>Equipment</SectionHead>
        <StatRow label="Basic Extractor" value="✓ Installed" valueColor="var(--green)" />
        {[
          { id: 'rosinPress', label: 'Rosin Press', requires: 'E1 research' },
          { id: 'bhoExtractor', label: 'BHO Extractor', requires: 'E2 research' },
        ].map(({ id, label, requires }) => {
          const e = eq[id];
          const canBuy = id === 'rosinPress' ? hasE1 : hasE2;
          return (
            <div key={id} style={{ marginBottom: 6 }}>
              <StatRow
                label={label}
                value={e?.installed ? '✓ Installed' : `$${(e?.cost ?? 0).toLocaleString()}`}
                valueColor={e?.installed ? 'var(--green)' : 'var(--text-muted)'}
              />
              {!e?.installed && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ width: '100%', marginTop: 2 }}
                  disabled={!canBuy || state.cash < (e?.cost ?? 0)}
                  onClick={() => dispatch({ type: 'BUY_PROCESSING_EQUIPMENT', payload: { equipId: id } })}
                >
                  {canBuy ? 'Purchase' : `Requires ${requires}`}
                </button>
              )}
            </div>
          );
        })}

        <SectionHead>Queue ({queue.length}/5)</SectionHead>
        {queue.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Queue empty. Add inventory below.</div>
        )}
        {queue.map(job => (
          <div key={job.inventoryId} style={{ background: 'var(--border-light)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 12 }}>
                {job.mode === 'packaging' ? '📦 Packaging' : `💎 ${job.extractType ?? 'Extract'}`}
              </span>
              <span style={{ fontSize: 11, color: job.status === 'active' ? 'var(--green)' : 'var(--text-muted)' }}>
                {job.status === 'active' ? `Day ${job.daysAccumulated}/${job.daysRequired}` : 'Pending'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{job.inputGrams}g input</div>
            {job.status === 'active' && (
              <MiniBar value={(job.daysAccumulated / job.daysRequired) * 100} color="var(--blue)" />
            )}
            <button
              className="btn btn-secondary btn-sm"
              style={{ width: '100%', marginTop: 6 }}
              onClick={() => dispatch({ type: 'CANCEL_PROCESSING', payload: { inventoryId: job.inventoryId } })}
            >
              Cancel
            </button>
          </div>
        ))}

        {queue.length < 5 && available.length > 0 && (
          <>
            <SectionHead>Add to Queue</SectionHead>
            {available.map(item => (
              <div key={item.id} style={{ marginBottom: 6, background: 'var(--border-light)', borderRadius: 6, padding: '6px 8px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {item.strainId.replace(/-/g, ' ')} · {item.grams}g
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ flex: 1 }}
                    onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'packaging', extractType: null } })}
                  >
                    📦 Package
                  </button>
                  {hasE1 && hasRosin && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ flex: 1 }}
                      onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'rosin' } })}
                    >
                      💎 Rosin
                    </button>
                  )}
                  {hasE2 && hasBHO && (
                    <>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'wax' } })}
                      >
                        Wax
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => dispatch({ type: 'ADD_TO_PROCESSING', payload: { inventoryId: item.id, mode: 'extract', extractType: 'shatter' } })}
                      >
                        Shatter
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // ── MARKETING OFFICE MODE ──────────────────────────────────────────────────
  function renderMarketing() {
    const mkt = state.marketing || {};
    const brand = state.brandValue ?? 0;
    const demand = state.demandModifier ?? 1.0;
    const campaigns = mkt.activeCampaigns || [];
    const tours = mkt.scheduledTours || [];
    const crises = mkt.prCrises || [];
    const saturation = mkt.socialSaturation || 0;

    return (
      <div style={{ padding: '0 16px 20px' }}>
        <SectionHead>Brand & Demand</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
          <EnvCell icon="🏷️" label="Brand" value={brand} color={brand >= 50 ? 'var(--green)' : brand >= 25 ? 'var(--yellow)' : 'var(--red)'} />
          <EnvCell icon="📈" label="Demand" value={`×${demand.toFixed(2)}`} color={demand >= 1.0 ? 'var(--green)' : 'var(--yellow)'} />
        </div>
        <StatRow label="Weekly Budget" value={`$${(mkt.weeklyBudget || 0).toLocaleString()}`} valueColor="var(--yellow)" />
        <StatRow label="Saturation" value={`${saturation}/10`} valueColor={saturation > 5 ? 'var(--red)' : saturation > 3 ? 'var(--yellow)' : 'var(--text-muted)'} />

        <SectionHead>Active Campaigns ({campaigns.length}/3)</SectionHead>
        {campaigns.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>No campaigns running.</div>
        )}
        {campaigns.map(c => (
          <div key={c.id} style={{ background: 'var(--border-light)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 12 }}>{c.name || c.templateId}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {c.daysRemaining}d left
              </span>
            </div>
            <MiniBar value={((c.totalDays || c.daysRemaining) - c.daysRemaining) / (c.totalDays || c.daysRemaining) * 100} color="#d97706" />
          </div>
        ))}

        {tours.length > 0 && (
          <>
            <SectionHead>Scheduled Tours</SectionHead>
            {tours.map(t => (
              <div key={t.id} style={{ background: 'var(--border-light)', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <StatRow label="Resolves" value={`Day ${t.scheduledDay}`} valueColor="var(--text-muted)" />
              </div>
            ))}
          </>
        )}

        {crises.length > 0 && (
          <>
            <SectionHead>PR Crises ({crises.length})</SectionHead>
            {crises.map(c => (
              <div key={c.id} style={{ background: '#1a0808', borderRadius: 6, padding: '8px 10px', marginBottom: 6, border: '1px solid var(--red)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)' }}>{c.description || 'Crisis'}</div>
              </div>
            ))}
          </>
        )}

        <div style={{ marginTop: 16 }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%', background: '#92400e', borderColor: '#d97706' }}
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'marketing' } })}
          >
            Open Marketing Desk →
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN ROOM MODE ────────────────────────────────────────────────────────
  function renderRoom() {
    if (!room) return (
      <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Room data unavailable.</div>
    );

    const env  = room.environment || {};
    const rec  = room.recipe || {};
    const eq   = room.equipment || {};
    const batch = room.batch;
    const vpd  = calculateVPD(env.temperature || 24, env.humidity || 60);

    const assignedWorkers = state.workers.filter(w =>
      (room.assignedWorkers || []).includes(w.id)
    );

    return (
      <div style={{ padding: '0 16px 20px' }}>

        {/* ── Environment ── */}
        <SectionHead>Environment</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
          <EnvCell
            icon="🌡️" label="Temp"
            value={`${env.temperature?.toFixed(1)}°C`}
            color={envColor(env.temperature, rec.temperature)}
          />
          <EnvCell
            icon="💧" label="Humidity"
            value={`${Math.round(env.humidity || 0)}%`}
            color={envColor(env.humidity, rec.humidity)}
          />
          <EnvCell
            icon="💨" label="VPD"
            value={`${vpd.toFixed(2)} kPa`}
            color={vpd >= 0.8 && vpd <= 1.2 ? 'var(--green)' : vpd >= 0.5 && vpd <= 1.5 ? 'var(--yellow)' : 'var(--red)'}
          />
          <EnvCell
            icon="🟢" label="CO₂"
            value={`${Math.round(env.co2 || 400)} ppm`}
            color={env.co2 >= 600 ? 'var(--green)' : 'var(--text-muted)'}
          />
        </div>

        {/* ── Batch status ── */}
        <SectionHead>Batch</SectionHead>
        {batch ? (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              <span className={`batch-stage-badge ${batch.stage}`} style={{ fontSize: 10 }}>
                {batch.stage.toUpperCase()}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center' }}>
                {batch.strainId?.replace(/-/g, ' ')} · {batch.plantCount} plants
              </span>
            </div>

            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>Health</span>
                <span style={{ fontWeight: 600 }}>{Math.round(batch.health)}%</span>
              </div>
              <MiniBar value={batch.health} />
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>THC</span>
                <span style={{ fontWeight: 600, color: 'var(--purple)' }}>{batch.thcAccumulation?.toFixed(1)}%</span>
              </div>
              <MiniBar value={(batch.thcAccumulation || 0) / 30 * 100} color="var(--purple)" />
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>Terpenes</span>
                <span style={{ fontWeight: 600, color: 'var(--yellow)' }}>{Math.round(batch.terpeneProfile)}</span>
              </div>
              <MiniBar value={batch.terpeneProfile} color="var(--yellow)" />
            </div>

            {batch.pestRisk > 0 && (
              <StatRow label="🐛 Pest Risk" value={`${Math.round(batch.pestRisk)}%`} valueColor={batch.pestRisk > 25 ? 'var(--red)' : 'var(--yellow)'} />
            )}
            {batch.moldRisk > 0 && (
              <StatRow label="💧 Mold Risk" value={`${Math.round(batch.moldRisk)}%`} valueColor={batch.moldRisk > 20 ? 'var(--red)' : 'var(--yellow)'} />
            )}
            <StatRow label="Projected Yield" value={`${batch.projectedGrams}g`} valueColor="var(--green)" />

            {batch.stage === 'harvest' && (
              <button
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 10 }}
                onClick={() => {
                  dispatch({ type: 'HARVEST_BATCH', payload: { roomId: room.id } });
                  onClose();
                }}
              >
                ✂️ Harvest Now
              </button>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
            Room empty — no active batch.
            <div style={{ marginTop: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: room.id } })}
              >
                Plant Batch →
              </button>
            </div>
          </div>
        )}

        {/* ── Quick Actions ── */}
        <SectionHead>Quick Actions</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
          <button
            className="btn btn-secondary btn-sm"
            disabled={!batch}
            onClick={() => dispatch({ type: 'WORKER_TASK', payload: { roomId: room.id, task: 'defoliate' } })}
          >
            🍃 Defoliate
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={!batch || (batch?.pestRisk || 0) < 5}
            onClick={() => dispatch({ type: 'WORKER_TASK', payload: { roomId: room.id, task: 'treat_pests' } })}
          >
            🐛 Treat Pests
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={!(eq.lights?.installed)}
            onClick={() => dispatch({ type: 'TOGGLE_LIGHT', payload: { roomId: room.id } })}
          >
            {env.lightOn ? '🌙 Lights Off' : '💡 Lights On'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: room.id } })}
          >
            ⚙️ Full View
          </button>
        </div>

        {/* ── Workers ── */}
        <SectionHead>Workers ({assignedWorkers.length})</SectionHead>
        {assignedWorkers.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>No workers assigned.</div>
        ) : (
          assignedWorkers.map(w => (
            <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, padding: '5px 8px', background: 'var(--border-light)', borderRadius: 6, fontSize: 11 }}>
              <div>
                <span style={{ fontWeight: 600 }}>{w.name}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 6, textTransform: 'capitalize' }}>{w.type}</span>
              </div>
              <span style={{ color: 'var(--text-muted)' }}>${w.currentWage}/wk</span>
            </div>
          ))
        )}
        <button
          className="btn btn-secondary btn-sm"
          style={{ width: '100%', marginBottom: 4 }}
          onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'workforce' } })}
        >
          + Assign Worker
        </button>

        {/* ── Equipment ── */}
        <SectionHead>Equipment</SectionHead>
        {[
          { key: 'hvac',       label: '⚙️ HVAC',       icon: null },
          { key: 'lights',     label: '💡 Lights',     icon: null },
          { key: 'irrigation', label: '💧 Irrigation', icon: null },
          { key: 'co2sys',     label: '💨 CO₂ System', icon: null },
        ].map(({ key, label }) => {
          const equip = eq[key];
          if (!equip) return null;
          const health = equip.health ?? 100;
          // most equipment doesn't have 'installed' field except lights/co2
          if (key === 'co2sys' && !equip.installed) return (
            <div key={key} style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
              {label} — <span style={{ color: 'var(--text-dim)' }}>not installed</span>
            </div>
          );
          return (
            <div key={key} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ color: health < 40 ? 'var(--red)' : health < 70 ? 'var(--yellow)' : 'var(--green)', fontWeight: 600 }}>
                  {Math.round(health)}%
                  {health < 70 && (
                    <button
                      className="btn btn-warn btn-sm"
                      style={{ marginLeft: 6, padding: '1px 5px', fontSize: 10 }}
                      disabled={state.cash < 200}
                      onClick={() => dispatch({ type: 'REPAIR_EQUIPMENT', payload: { roomId: room.id, equipType: key } })}
                    >
                      Repair $200
                    </button>
                  )}
                </span>
              </div>
              <MiniBar value={health} />
            </div>
          );
        })}
      </div>
    );
  }

  // ── Determine which content to show ──────────────────────────────────────
  function renderContent() {
    if (!roomDef) return null;
    if (!room || !room.unlocked) return renderLocked();
    if (roomDef.type === 'lab')        return renderLab();
    if (roomDef.type === 'curing')     return renderCuring();
    if (roomDef.type === 'processing') return renderProcessing();
    if (roomDef.type === 'marketing')  return renderMarketing();
    return renderRoom();
  }

  // ── Header ────────────────────────────────────────────────────────────────
  function renderHeader() {
    if (!roomDef) return null;
    const isLocked = !room || !room.unlocked;
    const displayName = isLocked
      ? (roomDef.unlockLabel || roomDef.id)
      : (room?.name || roomDef.type);

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: isLocked ? 'var(--text-muted)' : 'var(--text)' }}>
            {isLocked ? '🔒 ' : ''}{displayName}
          </div>
          {!isLocked && room && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
              {roomDef?.type === 'marketing'
                ? `${(state.marketing?.activeCampaigns || []).length} campaigns`
                : room.type === 'curing'
                ? `${(room.curingSlots ?? []).length}/4 slots`
                : room.type === 'processing'
                ? `${(room.processingQueue ?? []).length} in queue`
                : room.batch ? `${room.batch.plantCount} plants` : 'empty'}
            </div>
          )}
        </div>
        <button
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '2px 4px' }}
          onClick={onClose}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: 260,
      background: 'var(--bg-panel, #161b22)',
      borderLeft: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      transform: `translateX(${translateX})`,
      transition: 'transform 0.22s ease',
      zIndex: 10,
      boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.4)' : 'none',
      overflow: 'hidden',
    }}>
      {renderHeader()}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {renderContent()}
      </div>
    </div>
  );
}
